import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2, Plus, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Field, Loader, PageHeader } from "@/components/common";
import { KidAvatar } from "@/components/Avatars";

const RED = "var(--glow-media)";

function Tile({ t, i }) {
  return (
    <a href={t.url} target="_blank" rel="noopener noreferrer" data-testid={`shortcut-tile-${t.id}`}
      className="group relative flex aspect-[4/3] flex-col justify-end overflow-hidden rounded-2xl p-4 text-white transition-[transform,box-shadow] duration-200 hover:-translate-y-1 q-rise"
      style={{ background: `linear-gradient(150deg, ${t.color}, ${t.color}cc 55%, #0b0b0f)`, boxShadow: `0 12px 30px -12px ${t.color}`, animationDelay: `${i * 35}ms` }}>
      <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-white/70 transition-transform duration-200 group-hover:scale-150" />
      <span className="font-display text-xl font-extrabold leading-tight drop-shadow">{t.name}</span>
    </a>
  );
}

function Customize({ hubId, all, onDone }) {
  const [rows, setRows] = useState(all);
  const [c, setC] = useState({ name: "", url: "" });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const move = (i, d) => setRows((r) => { const x = [...r]; [x[i], x[i + d]] = [x[i + d], x[i]]; return x; });
  const save = async () => {
    setBusy(true);
    try { await api.put(`/organizations/${hubId}/media/shortcuts/prefs`, { order: rows.map((r) => r.id), hidden: rows.filter((r) => r.hidden).map((r) => r.id) }); toast.success("Shortcuts saved"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (!c.name.trim()) return setErr({ name: "Give the shortcut a name" });
    if (!/^https:\/\/[^\s/$.?#][^\s]*\.[^\s]+$/.test(c.url.trim())) return setErr({ url: "Enter a web address starting with https://" });
    try { const { data } = await api.post(`/organizations/${hubId}/media/shortcuts/custom`, c); setRows(data.all); setC({ name: "", url: "" }); toast.success(`${c.name} added`); } catch (x) { const d = x?.response?.data?.detail; d?.field ? setErr({ [d.field]: d.message }) : toast.error(errMsg(x)); }
  };
  const remove = async (id) => { try { const { data } = await api.delete(`/organizations/${hubId}/media/shortcuts/custom/${id}`); setRows(data.all); } catch (x) { toast.error(errMsg(x)); } };
  return (
    <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]" data-testid="shortcuts-customize">
      <ul className="q-card space-y-1 p-3">
        {rows.map((r, i) => (
          <li key={r.id} className="flex items-center gap-2 rounded-xl px-2 py-1" data-testid={`customize-row-${r.id}`}>
            <span className="h-6 w-6 flex-shrink-0 rounded-md" style={{ background: r.color }} />
            <span className={`flex-1 truncate font-semibold ${r.hidden ? "text-q-faint line-through" : "text-q-text"}`}>{r.name}</span>
            <button className="q-btn q-btn-ghost h-11 w-11 p-0" aria-label={r.hidden ? "Show" : "Hide"} onClick={() => setRows((x) => x.map((y) => (y.id === r.id ? { ...y, hidden: !y.hidden } : y)))} data-testid={`customize-toggle-${r.id}`}>{r.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            <button className="q-btn q-btn-ghost h-11 w-11 p-0" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`customize-up-${r.id}`}><ArrowUp className="h-4 w-4" /></button>
            <button className="q-btn q-btn-ghost h-11 w-11 p-0" aria-label="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)} data-testid={`customize-down-${r.id}`}><ArrowDown className="h-4 w-4" /></button>
            {r.custom && <button className="q-btn q-btn-ghost h-11 w-11 p-0" aria-label="Delete shortcut" onClick={() => remove(r.id)} data-testid={`customize-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
      </ul>
      <div className="space-y-4">
        <form onSubmit={add} noValidate className="q-card space-y-3" data-testid="custom-shortcut-form">
          <h2 className="font-display text-lg font-extrabold text-q-text">Add your own</h2>
          <Field label="Name" htmlFor="cs-name" error={err.name} errorTestid="custom-name-error"><input id="cs-name" className="q-input" value={c.name} onChange={(e) => { setC({ ...c, name: e.target.value }); setErr({}); }} data-testid="custom-name-input" /></Field>
          <Field label="Web address" htmlFor="cs-url" error={err.url} errorTestid="custom-url-error"><input id="cs-url" className="q-input" placeholder="https://" value={c.url} aria-invalid={!!err.url} onChange={(e) => { setC({ ...c, url: e.target.value }); setErr({}); }} data-testid="custom-url-input" /></Field>
          <button className="q-btn q-btn-secondary" data-testid="custom-add-btn"><Plus className="h-4 w-4" /> Add shortcut</button>
        </form>
        <div className="flex gap-2">
          <button className="q-btn q-btn-secondary" onClick={onDone} data-testid="customize-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary" disabled={busy} onClick={save} data-testid="customize-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

function KidsShortcuts({ hubId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/organizations/${hubId}/media/kids`).then(({ data }) => setD(data)); }, [hubId]);
  if (!d) return null;
  const toggle = async (k, t) => {
    const allowed = k.allowed.includes(t) ? k.allowed.filter((x) => x !== t) : [...k.allowed, t];
    try { await api.put(`/organizations/${hubId}/media/kids/${k.id}`, { allowed }); setD((x) => ({ ...x, kids: x.kids.map((y) => (y.id === k.id ? { ...y, allowed } : y)) })); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mt-10" data-testid="kids-shortcuts">
      <h2 className="mb-3 font-display text-xl font-extrabold text-q-text">Kids' shortcuts</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {d.kids.map((k) => (
          <div key={k.id} className="q-card" data-testid={`kids-shortcuts-${k.name.toLowerCase()}`}>
            <p className="mb-3 flex items-center gap-2 font-display font-extrabold text-q-text"><KidAvatar avatar={k.avatar} size={32} /> {k.name}</p>
            <div className="flex flex-wrap gap-2">
              {d.tiles.map((t) => { const on = k.allowed.includes(t.id); return <button key={t.id} aria-pressed={on} onClick={() => toggle(k, t.id)} data-testid={`kid-allow-${k.name.toLowerCase()}-${t.id}`}
                className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold transition-colors duration-150 ${on ? "border-transparent text-white" : "border-q-line text-q-muted"}`} style={on ? { background: t.color } : undefined}>{t.name}</button>; })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Shortcuts() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/media/shortcuts`).then(({ data }) => setD(data)), [hub.id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader />;
  return (
    <div data-testid="shortcuts-page">
      <PageHeader eyebrow="Media" title="Shortcuts" rgb={RED} testid="shortcuts-title"
        actions={!d.is_kid && !edit && !hub.read_only && <button className="q-btn q-btn-secondary" onClick={() => setEdit(true)} data-testid="shortcuts-customize-btn"><Settings2 className="h-4 w-4" /> Customize</button>} />
      {edit ? <Customize hubId={hub.id} all={d.all} onDone={() => { setEdit(false); load(); }} /> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="shortcuts-grid">{d.tiles.map((t, i) => <Tile key={t.id} t={t} i={i} />)}</div>
      )}
      {d.is_kid && !d.tiles.length && <p className="text-q-muted" data-testid="shortcuts-kid-empty">Ask a grown-up to pick your shortcuts.</p>}
      {d.is_parent && !edit && <KidsShortcuts hubId={hub.id} />}
    </div>
  );
}
